import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingIndicator } from 'lib/components';
import { useHistory, useLocation } from 'react-router-dom';

import { VideoMerge } from './videoMerge';
import {
  fetchVideo,
  initLibrary,
  mergeVideos,
  useGlobalDispatch,
} from 'lib/context';
import { useVideosContext } from 'lib/context/VideosContext';
import {
  ModalMergeCreateNew,
  ModalVideoMergeListVideo,
} from 'lib/components/modal';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import RouteLeavingGuard from '../../videoDetails/main/RouteLeavingGuard';
import { NotFound } from 'app/pages/notFound/NotFound';
import { errorToast } from 'lib/components/toasts/error';
import { VIDEOS_ACTIONS } from 'lib/context/videosReducer';
import { AddRecordingToMerge } from '../components/AddRecordingToMerge';
import { v4 as uuidv4 } from 'uuid';
import { EXTERNAL_VIDEO_PREFIX } from 'app/pages/record/const';

const MainContainer = styled.main`
  overflow-x: hidden;
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 0;
  min-height: 500px;
  ${theme.mediaQueryMinWidth.mb} {
    padding: 64px 24px 84px 24px;
  }
`;

const { MERGE_VIDEO_ERROR } = VIDEOS_ACTIONS;

export const Main = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const ids = (urlParams.get('videos') || '').split(',').filter(Boolean);
  const [videosToMerge, setVideosToMerge] = useState(ids.map(id => ({ id })));
  const {
    state: { videos },
    dispatch,
  } = useVideosContext();
  const history = useHistory();
  const { dispatch: global } = useGlobalDispatch();
  const [showMergeModal, setMergeModal] = React.useState(false);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [errorFetchingVideos, setErrorFetchingVideos] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [showRecordHome, setShowRecordHome] = useState(false);

  useEffect(() => {
    const checkIfAllVideosAreFetched = () => {
      for (let i = 0; i < videosToMerge.length; i++) {
        // set error if not found and not external
        if (
          !videos[videosToMerge[i].id] &&
          !videosToMerge[i].id.toString().includes(EXTERNAL_VIDEO_PREFIX)
        ) {
          setErrorFetchingVideos(true);
          return;
        }
      }
      setErrorFetchingVideos(false);
    };
    const getVideos = async () => {
      setLoading(true);
      for (const v of videosToMerge) {
        // don't fetch fetched videos or external videos without id
        if (
          videos[v.id] ||
          (v?.id && v.id.toString().includes(EXTERNAL_VIDEO_PREFIX))
        ) {
          continue;
        }
        await fetchVideo({ videoId: v.id, dispatch });
      }
      checkIfAllVideosAreFetched();
      setLoading(false);
    };

    getVideos();
  }, [videosToMerge]);

  useEffect(() => {
    if (!videosToMerge || !videosToMerge.length) {
      setMergeModal(true);
    }
  }, [videosToMerge]);

  useEffect(() => {
    Object.values(videos).forEach((video: any) => {
      setVideosToMerge((videos: any) => {
        const vtm = [...videos];
        return vtm.map(v => {
          if (v.id === video.id) {
            return {
              ...video,
              ...v,
              source: video.videoSource,
              flvName: video.flvName + '.mp4',
              start: '00:00:00:00',
            };
          }
          return v;
        });
      });
    });
  }, [videos]);

  const handleMergeVideos = async (videoName: string) => {
    const duration =
      1000 *
      videosToMerge.reduce((sum: number, v: any) => sum + v.trimDuration, 0);
    const videos = videosToMerge.map((v: any) => ({
      start: v.start,
      end: v.end,
      id: v.id,
      flvName: v.flvName,
    }));
    await mergeVideos({
      data: {
        videos,
        videoName,
        duration,
      },
      dispatch: (obj: { type: string; error: string }) => {
        if (obj.type === MERGE_VIDEO_ERROR) {
          errorToast({
            title: `Error in merging videos!`,
          });
        }
      },
    });
    setSaved(true);
    initLibrary({ dispatch: global });
    history.push('/home?refresh=true');
  };

  const handleAddExternalVideo = (videoSource: string) => {
    const id = `${EXTERNAL_VIDEO_PREFIX}${uuidv4()}`;
    setVideosToMerge(prev => [
      ...prev,
      {
        id,
        videoSource,
        source: videoSource,
        flvName: videoSource,
        start: '00:00:00:00',
      },
    ]);
    setShowRecordHome(false);
    setMergeModal(false);
  };

  if (loading) {
    return <LoadingIndicator isLoading={loading} />;
  }

  if (errorFetchingVideos) {
    return <NotFound />;
  }

  return (
    <MainContainer>
      <RouteLeavingGuard
        when={true}
        navigate={path => history.push(path)}
        stay={true}
        onStay={() => setShowSaveModal(true)}
        onConfirm={() => {}}
        shouldBlockNavigation={() => {
          if (!saved || videosToMerge.length === 0) {
            return true;
          }
          return false;
        }}
      />
      <VideoMerge
        openModal={() => setMergeModal(true)}
        handleShowRecordScreen={() => setShowRecordHome(true)}
        videos={videosToMerge}
        setVideos={setVideosToMerge}
        mergeVideos={() => setShowSaveModal(true)}
      />
      {showMergeModal && (
        <ModalVideoMergeListVideo
          handleSelectedVideos={(selectedVideos = []) => {
            setVideosToMerge([
              ...videosToMerge,
              ...selectedVideos.map((v: number) => {
                const selectedVideo = videos[v] || {};
                return {
                  ...selectedVideo,
                  id: v,
                  source: selectedVideo.videoSource,
                  flvName: selectedVideo.flvName + '.mp4',
                };
              }),
            ]);
            setMergeModal(false);
          }}
          onSubmitExternal={handleAddExternalVideo}
          handleModalClose={() => setMergeModal(false)}
          title='Add More Videos'
          showHelpdesk={false}
          showExternalSources={true}
        />
      )}
      {showSaveModal && videosToMerge.length !== 0 && (
        <ModalMergeCreateNew
          initTitle={''}
          handleSubmit={title => handleMergeVideos(title)}
          handleModalClose={() => setShowSaveModal(false)}
        />
      )}
      {showRecordHome && (
        <AddRecordingToMerge
          handleShowRecordHome={setShowRecordHome}
          onRecordingUploaded={handleAddExternalVideo}
        />
      )}
    </MainContainer>
  );
};
